import {MenuProvider, MenuButton, MenuButtonArrow, Menu, MenuItem} from '@ariakit/react'
import {faArrowUpRightFromSquare, faQuestionCircle} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {getFAQLink} from '@freckle/educator-materials/ts/components/support-button/links'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {questionIcon, helpText, helpOptions, icon, button} from './style.module.scss'

type Props = {
  supportLink: string
  countryCode: string | null | undefined
}

export function TeacherSupportButton({countryCode, supportLink}: Props) {
  const {t} = useSafeTranslation()

  const supportNode =
    countryCode === 'USA' ? <SupportChat /> : <SupportTicket supportLink={supportLink} />

  return (
    <div id="navbar-help" className={helpOptions}>
      <MenuProvider>
        <MenuButton className={button}>
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faQuestionCircle} className={questionIcon} />
            <span className={helpText}>&nbsp;{t('SUPPORT_BUTTON_SHORT_HELP_TEXT')}&nbsp;</span>
            <MenuButtonArrow />
          </Flex>
        </MenuButton>
        <Menu>
          <MenuItem
            render={
              <a
                target="_blank"
                data-test="support-menu-help-articles-link"
                href={getFAQLink(countryCode)}
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={icon} />
                &nbsp;
                <SafeTrans i18nKey="SUPPORT_MENU_HELP_ARTICLES_ITEM_LINK_TEXT" />
              </a>
            }
          />
          {supportNode}
        </Menu>
      </MenuProvider>
    </div>
  )
}

const loadForethoughtScripts = () => {
  return new Promise<void>((resolve, reject) => {
    if (!document.getElementById('forethought-widget-embed-script')) {
      const forethoughtScript = document.createElement('script')
      forethoughtScript.src = 'https://solve-widget.forethought.ai/embed.js'
      forethoughtScript.id = 'forethought-widget-embed-script'
      forethoughtScript.type = 'application/javascript'
      forethoughtScript.dataset.apiKey = '5791f610-77c3-450b-9cef-3b6c3419962d'
      forethoughtScript.dataset.ftWorkflowTag = 'Freckle_Platform'
      forethoughtScript.dataset.ftUrl = 'https://classroom.freckle.com'
      forethoughtScript.onload = () => resolve()
      forethoughtScript.onerror = () => reject('Failed to load Forethought script')
      document.body.appendChild(forethoughtScript)
    } else {
      resolve() // Script already loaded
    }
  })
}

const loadSalesforceScripts = () => {
  return new Promise<void>((resolve, reject) => {
    if (!document.getElementById('salesforce-bootstrap-script')) {
      // Create and load the Salesforce bootstrap script
      const salesforceScript = document.createElement('script')
      salesforceScript.src =
        'https://renlearn.my.site.com/ESWRLIProductSupportCh1733220094443/assets/js/bootstrap.min.js'
      salesforceScript.id = 'salesforce-bootstrap-script'
      salesforceScript.type = 'text/javascript'
      salesforceScript.onload = () => {
        try {
          // Initialize Embedded Messaging after script loads
          window.embeddedservice_bootstrap.settings.language = 'en_US'
          window.addEventListener('onEmbeddedMessagingReady', () => {
            console.log('Embedded Messaging is ready')
          })
          window.embeddedservice_bootstrap.init(
            '00DHr000001sf9h',
            'RLI_Product_Support_Chat',
            'https://renlearn.my.site.com/ESWRLIProductSupportCh1733220094443',
            {
              scrt2URL: 'https://renlearn.my.salesforce-scrt.com',
            }
          )
          resolve()
        } catch (err) {
          console.error('Error initializing Embedded Messaging:', err)
          reject(err)
        }
      }
      salesforceScript.onerror = () => reject('Failed to load Salesforce script')
      document.body.appendChild(salesforceScript)
    } else {
      resolve() // Script already loaded
    }
  })
}

const openSupportChat = async (): Promise<void> => {
  try {
    await loadForethoughtScripts()
    await loadSalesforceScripts()
    if (window.Forethought !== 'undefined') {
      window.Forethought('widget', 'show')
      window.Forethought('widget', 'open')
    } else {
      console.error('Forethought widget is not initialized')
    }
  } catch (error) {
    console.error(error)
  }
}

function SupportChat() {
  return (
    <MenuItem data-test="support-menu-support-chat" onClick={openSupportChat}>
      <SafeTrans i18nKey="SUPPORT_MENU_SUPPORT_CHAT" />
    </MenuItem>
  )
}

type SupporTicketProps = {
  supportLink: string
}

function SupportTicket({supportLink}: SupporTicketProps) {
  return (
    <MenuItem
      data-test="support-menu-support-ticket-link"
      render={
        <a href={supportLink} target="_blank" rel="noopener noreferrer">
          <SafeTrans i18nKey="SUPPORT_MENU_SUPPORT_TICKET_ITEM_LINK_TEXT" />
        </a>
      }
    />
  )
}
